/**
 * Föreningens kontaktuppgifter.
 *
 * De stod tidigare inskrivna i integritetspolicyn, vilket betydde att ett
 * ordförandebyte krävde en kodändring och en driftsättning. Styrelsen byts
 * varje år; koden gör det inte.
 *
 * Bara uppgifterna ligger här, inte texten omkring dem. Policyns innehåll är
 * påståenden om vad koden faktiskt gör – lagringstider, vilka leverantörer
 * som finns, vad som loggas – och den dagen texten går att redigera fritt
 * börjar den glida isär från verkligheten utan att någon märker det. Namnet
 * på den som svarar i telefon är däremot ingen utsaga om systemet.
 */

export interface ContactSettings {
  /** Organisationsnummer, som det står hos Skatteverket. */
  orgNumber: string
  /** Föreningsnummer hos Riksidrottsförbundet. */
  federationNumber: string
  /** Den som svarar på frågor om personuppgifter. */
  dataContactName: string
  dataContactRole: string
  dataContactEmail: string
  dataContactPhone: string
  /** Allmän adress, den appen hänvisar till när något gått fel. */
  officeEmail: string
}

/**
 * Värdena som gäller tills någon ändrat dem.
 *
 * De är också reservvärden: saknas dokumentet, eller går det inte att läsa,
 * visar appen de här i stället för en tom ruta. En integritetspolicy utan
 * kontaktuppgift är sämre än en med en gammal.
 */
export const DEFAULT_CONTACT: ContactSettings = {
  orgNumber: '897001-9272',
  federationNumber: '35500-15',
  dataContactName: 'Kicki Blomquist',
  dataContactRole: 'ordförande',
  dataContactEmail: 'ordforande@lirabk.se',
  dataContactPhone: '070-377 85 87',
  officeEmail: 'kansli@lirabk.se',
}

export function withContactDefaults(stored: Partial<ContactSettings> | undefined): ContactSettings {
  const trimmed = Object.fromEntries(
    Object.entries(stored ?? {})
      .filter(([, value]) => typeof value === 'string' && value.trim().length > 0)
      .map(([key, value]) => [key, (value as string).trim()]),
  )
  return { ...DEFAULT_CONTACT, ...trimmed }
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/**
 * Kontrollerar det som faktiskt går att skriva fel.
 *
 * Adresserna är de enda fälten appen gör något med – de blir länkar man
 * klickar på – och en felstavad adress syns inte förrän någon undrar varför
 * ingen svarar. Namn och nummer kontrolleras bara mot att vara tomma, för
 * föreningsnummer och telefonnummer skrivs på fler sätt än ett mönster
 * orkar täcka, och ett formulär som nekar ett korrekt nummer är värre än ett
 * som släpper igenom ett konstigt.
 */
export function validateContact(input: ContactSettings): string | null {
  const required: [keyof ContactSettings, string][] = [
    ['orgNumber', 'Organisationsnummer'],
    ['federationNumber', 'Föreningsnummer'],
    ['dataContactName', 'Namn på kontaktpersonen'],
    ['dataContactRole', 'Kontaktpersonens roll'],
    ['dataContactPhone', 'Telefonnummer'],
  ]

  for (const [key, label] of required) {
    if (!input[key]?.trim()) return `${label} får inte vara tomt.`
  }

  if (!EMAIL.test(input.dataContactEmail?.trim() ?? '')) {
    return 'E-postadressen till kontaktpersonen ser inte riktig ut.'
  }
  if (!EMAIL.test(input.officeEmail?.trim() ?? '')) {
    return 'Kansliets e-postadress ser inte riktig ut.'
  }

  return null
}
